import React, { FunctionComponent, ReactNode } from 'react';
import { SbBlokData, storyblokEditable } from '@storyblok/react';
import classnames from 'classnames';
import { graphql, useStaticQuery } from 'gatsby';

import CountryPickerBanner from './CountryPickerBanner';
import { Section } from './Section';
import { renderComponent } from './';

interface GlobalProps {
  id: string;
  customFooterContent?: ReactNode;
}

declare global {
  interface Window {
    ire: any;
  }
}

interface GlobalBlok extends SbBlokData {
  header: SbBlokData[];
  footer: SbBlokData[];
  hasNavigation: boolean;
}

interface AllStoryblokEntry {
  allStoryblokEntry: {
    edges: [
      {
        node: {
          uuid: string;
          content: string;
        };
      }
    ];
  };
}

const headerClassNames = ({ hasNavigation }: Partial<GlobalBlok>): string =>
  classnames({
    'bg-white lg:pb-4': hasNavigation,
    'w-full bg-white py-4': !hasNavigation
  });

export const Global: FunctionComponent<React.PropsWithChildren<GlobalProps>> = ({
  id,
  children,
  customFooterContent
}) => {
  const { allStoryblokEntry }: AllStoryblokEntry = useStaticQuery(
    graphql`
      query GlobalQuery {
        allStoryblokEntry(filter: { field_component: { eq: "global" } }) {
          edges {
            node {
              uuid
              content
            }
          }
        }
      }
    `
  );

  const global = allStoryblokEntry.edges.find(edge => edge.node.uuid === id);

  if (!global) {
    return null;
  }

  const blok: GlobalBlok = JSON.parse(global.node.content);
  const { header = [], footer = [] } = blok;

  return (
    <div role="main" className="overflow-hidden">
      <CountryPickerBanner />
      {header.length > 0 && (
        <Section
          width="md"
          className={headerClassNames(blok)}
          hasPaddingBottom={false}
          hasPaddingTop={false}
          blok={blok}
        >
          <div className="z-10 relative">
            {header.map(blok => (
              <div {...storyblokEditable(blok)} key={blok._uid}>
                {renderComponent({ blok }, blok.component)}
              </div>
            ))}
          </div>
        </Section>
      )}

      <div className="lg:relative">{children}</div>

      {footer.map(blok => (
        <div {...storyblokEditable(blok)} key={blok._uid}>
          {renderComponent({ blok, customFooterContent }, blok.component)}
        </div>
      ))}
    </div>
  );
};
